import React from 'react';
import { IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonIcon, IonText } from '@ionic/react';
import { pulseOutline } from 'ionicons/icons';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AdminDeviceMqtt } from '../../../domain/Admin';
import { RESET_REASON_LABEL, formatUptime, signalQuality, spacedHex, type DeviceHealthPoint } from '../../../domain/DeviceHealth';
import { formatRelative } from '../../../domain/WeatherStation';

const volts = (mv: number | null) => (mv === null ? null : Math.round(mv / 10) / 100);

const formatTick = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Un dato de salud (también lo usa la salud del hub).
export const Tile: React.FC<{
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  color?: string;
}> = ({ label, value, detail, color }) => (
  <div className="admin-stat-tile admin-health-tile">
    <span className="admin-stat-label">{label}</span>
    <span className="admin-stat-value" style={color ? { color: `var(--ion-color-${color})` } : undefined}>
      {value}
    </span>
    {detail && <span className="admin-stat-detail">{detail}</span>}
  </div>
);

// Batería y panel en volts, por hora, últimos 7 días: si la batería no
// llega a recuperarse de día, el panel no alcanza.
const HealthChart: React.FC<{ points: DeviceHealthPoint[] }> = ({ points }) => {
  const data = points.map((p) => ({
    at: p.at,
    battery: volts(p.batteryMv),
    solar: volts(p.solarMv),
  }));
  return (
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
        <CartesianGrid vertical={false} stroke="var(--domiot-border)" strokeDasharray="3 3" />
        <XAxis
          dataKey="at"
          tickFormatter={formatTick}
          minTickGap={32}
          tick={{ fontSize: 11, fill: 'var(--domiot-text-muted)' }}
          axisLine={{ stroke: 'var(--domiot-border)' }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--domiot-text-muted)' }}
          axisLine={false}
          tickLine={false}
          width={40}
          domain={[0, 'auto']}
          tickFormatter={(v: number) => `${v}V`}
        />
        <Tooltip
          labelFormatter={(iso) =>
            new Date(String(iso)).toLocaleString('es-AR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          }
          formatter={(v, name) => [`${v} V`, name === 'battery' ? 'Batería' : 'Panel']}
          contentStyle={{
            background: 'var(--domiot-surface)',
            border: '1px solid var(--domiot-border)',
            borderRadius: 10,
            fontSize: 12,
          }}
        />
        <Line type="monotone" dataKey="solar" stroke="var(--domiot-warning)" strokeWidth={1.5} dot={false} connectNulls={false} />
        <Line type="monotone" dataKey="battery" stroke="var(--domiot-accent)" strokeWidth={2} dot={false} connectNulls={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Lo que el equipo cuenta de sí mismo en <base>/info. Solo backoffice.
const DeviceHealthCard: React.FC<{ health: AdminDeviceMqtt['health'] }> = ({ health }) => {
  const { latest, hourly, frames } = health;
  if (!latest) {
    return (
      <IonCard className="admin-detail-card">
        <IonCardHeader>
          <IonCardTitle>
            <span className="admin-card-icon tone-red">
              <IonIcon icon={pulseOutline} />
            </span>
            Salud del equipo
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <p className="admin-list-row-meta">Todavía no llegó ningún mensaje de info (batería, señal, memoria…).</p>
        </IonCardContent>
      </IonCard>
    );
  }

  const h = latest.health;
  const reset = h.resetReason ? RESET_REASON_LABEL[h.resetReason] : null;
  const signal = h.rssi !== null ? signalQuality(h.rssi) : null;
  const lostPct = frames.received + frames.lost > 0 ? (100 * frames.lost) / (frames.received + frames.lost) : 0;
  const warnings = [
    h.lowBattery && (h.charging ? 'Batería baja, cargando.' : 'Batería baja.'),
    h.sensorError && 'Algún sensor no respondió en el último ciclo.',
    // Con hub LoRa la estación no tiene reloj, pero el hub manda "ts" con la
    // hora real de llegada: solo sin ts se guarda la hora en que llegó al backend.
    h.clockUnset &&
      typeof latest.payload?.ts !== 'number' &&
      'El equipo no tiene hora: las lecturas se guardan con la hora de llegada.',
  ].filter(Boolean) as string[];

  return (
    <IonCard className="admin-detail-card">
      <IonCardHeader>
        <IonCardTitle>
            <span className="admin-card-icon tone-red">
              <IonIcon icon={pulseOutline} />
            </span>
            Salud del equipo
          </IonCardTitle>
        <IonCardSubtitle>
          Último info {formatRelative(latest.recordedAt)}
          {h.firmware && ` · firmware ${h.firmware}`}
          {h.intervalMin ? ` · reporta cada ${h.intervalMin} min` : ''}
        </IonCardSubtitle>
      </IonCardHeader>
      <IonCardContent>
        {warnings.map((w) => (
          <IonText key={w} color="danger">
            <p>{w}</p>
          </IonText>
        ))}

        <div className="admin-health-grid">
          {h.batteryMv !== null && (
            <Tile
              label="Batería"
              value={h.batteryPct !== null ? `${h.batteryPct}%` : `${volts(h.batteryMv)} V`}
              detail={
                [h.batteryPct !== null && `${volts(h.batteryMv)} V`, h.charging && 'cargando'].filter(Boolean).join(' · ') ||
                undefined
              }
              color={h.lowBattery ? 'danger' : undefined}
            />
          )}
          {h.solarMv !== null && (
            <Tile label="Panel solar" value={`${volts(h.solarMv)} V`} detail={h.solarMv === 0 ? 'sin sol' : undefined} />
          )}
          {h.rssi !== null && signal && (
            <Tile
              label="Señal LoRa"
              value={`${h.rssi} dBm`}
              detail={`${signal.text}${h.snr !== null ? ` · SNR ${h.snr} dB` : ''}`}
              color={signal.color === 'success' ? undefined : signal.color}
            />
          )}
          {h.internalTempC !== null && <Tile label="Temp. de la caja" value={`${h.internalTempC} °C`} />}
          {h.freeHeapKb !== null && <Tile label="Memoria libre" value={`${h.freeHeapKb} KB`} />}
          {h.awakeMs !== null && (
            <Tile label="Despierto por ciclo" value={`${(h.awakeMs / 1000).toFixed(1)} s`} detail="lo que usa la CPU" />
          )}
          {h.uptimeS !== null && (
            <Tile
              label="Encendido hace"
              value={formatUptime(h.uptimeS)}
              detail={reset ? `último reinicio: ${reset.text}` : undefined}
              color={reset?.bad ? 'warning' : undefined}
            />
          )}
          {frames.received > 0 && (
            <Tile
              label="Tramas perdidas"
              value={`${lostPct.toFixed(1)}%`}
              detail={`${frames.lost} de ${frames.received + frames.lost} · 7 días`}
              color={lostPct > 10 ? 'danger' : lostPct > 3 ? 'warning' : undefined}
            />
          )}
        </div>

        {hourly.some((p) => p.batteryMv !== null) && (
          <>
            <p className="admin-list-row-meta admin-health-legend">
              <span style={{ background: 'var(--domiot-accent)' }} />
              Batería
              <span style={{ background: 'var(--domiot-warning)' }} />
              Panel · últimos 7 días
            </p>
            <HealthChart points={hourly} />
          </>
        )}

        <p className="admin-list-row-meta" style={{ marginTop: 12 }}>
          Lo que llegó
        </p>
        {latest.rawHex && (
          <code className="admin-mqtt-payload">
            {spacedHex(latest.rawHex)} ({latest.rawHex.length / 2} bytes)
          </code>
        )}
        {latest.payload && <code className="admin-mqtt-payload">{JSON.stringify(latest.payload)}</code>}
      </IonCardContent>
    </IonCard>
  );
};

export default DeviceHealthCard;
